import { classNames } from '@dimjs/utils';
import { hooks } from '@wove/react';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useScrollCallback } from '../hooks';
import { useDocumentEvent } from '../hooks/use-document-event';
import { ModalDrawer, ModalDrawerProps } from '../modal-drawer';
import { getScrollParent } from '../utils';
import { CommonPropsWithChildren, withNativeProps } from '../utils/native-props';
import './style.less';

export type DropMenuProps = {
  content: ReactElement | string;
  direction: 'bottom' | 'top';
  visible: boolean;
  onClose: () => void;
  modalDrawerProps?: Omit<ModalDrawerProps, 'visible' | 'direction' | 'onClose'>;
} & CommonPropsWithChildren;

export const DropMenu = (props: DropMenuProps) => {
  const position = props.direction === 'bottom' ? 'top' : 'bottom';
  const containerRef = useRef<any>(null);
  const [sizeNumber, setSizeNumber] = useState<number>(0);
  const modalBodyRef = useRef<HTMLDivElement>();
  const afterOpenRef = useRef(false);
  const windowSize = hooks.useWindowSize();
  const scrollElementRef = useRef<any>(null);
  const containerRefHandle = hooks.useCallbackRef((node: HTMLDivElement) => {
    if (node) {
      containerRef.current = node;
      scrollElementRef.current = getScrollParent(node, document.body);
    }
  });

  const modalBodyRefHandle = hooks.useCallbackRef((node: HTMLDivElement) => {
    if (node) {
      modalBodyRef.current = node;
    }
  });

  useDocumentEvent(
    () => {
      if (!afterOpenRef.current) return;
      afterOpenRef.current = false;
      props.onClose?.();
    },
    ['click'],
    [containerRef.current, modalBodyRef.current],
  );

  if (props.visible) {
    scrollElementRef.current?.classList.add('overflow-hidden');
  } else {
    scrollElementRef.current?.classList.remove('overflow-hidden');
  }

  const onAfterOpen = hooks.useCallbackRef(() => {
    afterOpenRef.current = true;
    props.modalDrawerProps?.onAfterOpen?.();
  });

  const modalDrawerStyle = props.modalDrawerProps?.style;

  const onScroll = hooks.useCallbackRef(() => {
    if (props.visible) {
      updateOffset();
    }
  });

  const updateOffset = hooks.useCallbackRef(() => {
    let size = sizeNumber;
    if (props.visible) {
      const rect = containerRef.current.getBoundingClientRect();
      // hotfix: 临时方案 解决因计算有小数位造成IOS设备出现缝隙
      if (props.direction === 'bottom') {
        size = Math.floor(rect.bottom as number);
      } else {
        size = Math.ceil((windowSize?.height || 0) - rect.top);
      }
    }
    setSizeNumber(size);
  });

  useEffect(() => {
    props.visible && updateOffset();
  }, [props.visible, updateOffset]);

  useScrollCallback(onScroll, document);

  const modalDrawerStyleMerge = useMemo(() => {
    return props.direction === 'bottom'
      ? { ...modalDrawerStyle, top: sizeNumber }
      : { ...modalDrawerStyle, bottom: sizeNumber, top: 0 };
  }, [modalDrawerStyle, props.direction, sizeNumber]);

  return withNativeProps(
    props,
    <div className="v-drop-menu">
      <div className="v-drop-menu-title" ref={containerRefHandle}>
        {props.children}
      </div>
      <ModalDrawer
        {...props.modalDrawerProps}
        className={classNames('v-drop-menu-content', props.modalDrawerProps?.className)}
        visible={props.visible}
        onClose={props.onClose}
        position={position}
        style={modalDrawerStyleMerge}
        onAfterOpen={onAfterOpen}
        bodyRef={modalBodyRefHandle}
      >
        {props.content}
      </ModalDrawer>
    </div>,
  );
};
